import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import {
  PlanBadge,
  DropdownMenu,
  Stack,
  Icon,
  tokens,
  getInputContainerStyles,
  Dropdown,
  Button,
  Divider,
} from '@hotjar/hotsauce';
import type { SiteAccountDTO } from '@hotjar/account';
import { usePlanRepository } from '@hotjar/webapp/hooks/usePlanRepository';
import { usePricingTableByAccount } from '@hotjar/webapp/hooks/usePricingTable';
import { usePlanAllowances } from '@hotjar/webapp/domains/revenue/hooks/usePlanAllowances';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { isScaleSelfServe } from '@hotjar/webapp/domains/revenue/plan';
import { mapSiteAccountDtoToModel } from '@hotjar/account';
// TODO: this is just an interim step... we should add supported accessibility to hotsauce dropdown
import { useTippy } from '@hotjar/webapp/hooks/useTippy';
import { getSalesLink } from '@hotjar/upsell';
import type { PlanSelectDropdownProps } from '../index';

export const S = {
  StyledDropdownMenu: styled(Dropdown)`
    width: 280px;
    max-height: 440px;
    list-style: none;

    /**
    * This hack is needed because we have a global rule for .tippy-content in insights.scss,
    * which applies extra padding, and it looks terrible with this dropdown, so we overwrite it.
    */
    .tippy-content {
      padding: 0;
    }
  `,
  SelectContainer: styled.button<Parameters<typeof getInputContainerStyles>[0]>`
    ${getInputContainerStyles};

    align-items: center;
    cursor: pointer;
    display: flex;
    height: ${tokens.spacer.x5};
    max-width: 280px;
    min-width: 200px;
    padding: ${tokens.spacer.x2};

    border: none;
    ${tokens.transition()};
    ${`${tokens.elevation(1)}, inset 0 0 0 1px ${tokens.color.ui_border}`};

    :hover {
      ${`${tokens.elevation(1)}, inset 0 0 0 1px ${tokens.color.ui_border_alt}`};
    }

    :focus {
      ${`${tokens.focus()}, inset 0 0 0 1px ${tokens.color.ui_border_active}`};
    }

    :focus-visible {
      outline: none;
    }
  `,
  DropdownItemIcon: styled(Icon)`
    flex-shrink: 0;
  `,
  TruncatedLabelContainer: styled.div`
    flex-grow: 1;
    overflow: hidden;
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  SelectedPlanBadge: styled(PlanBadge)`
    margin: 0 ${tokens.spacer.x1};
  `,
  ItemContainer: styled(Stack)`
    align-items: center;
    display: flex;
    justify-content: flex-start;
    width: 100%;
  `,
  Footer: styled.div`
    padding: ${tokens.spacer.x1} ${tokens.spacer.x2} ${tokens.spacer.x2};
  `,
};

export const PlanSelectDropdown = ({
  account,
  activePlanId,
  onChange,
  featureGroupFilter,
  hidePlanBadges,
}: PlanSelectDropdownProps): JSX.Element => {
  const { t } = useTranslation('revenue');
  const { t: tContactSales } = useTranslationWithPrefix('revenue:plan-select.contact-sales');
  const tippyInstance = useTippy();
  const planRepository = usePlanRepository();
  const { priceOrderedPlans } = usePricingTableByAccount(account);
  const { getPlanAllowances } = usePlanAllowances();
  const scaleNotSelfServe = !isScaleSelfServe(mapSiteAccountDtoToModel(account as SiteAccountDTO));
  const plans = priceOrderedPlans
    .map(([id]) => planRepository.byId(id))
    .filter((plan) => {
      if (plan.featureGroup === 'scale' && scaleNotSelfServe) {
        return false;
      }
      return featureGroupFilter ? plan.featureGroup === featureGroupFilter : plan;
    });
  const activePlan = plans.find((plan) => plan.identifier === activePlanId) ?? plans[0];

  return (
    <S.StyledDropdownMenu
      onCreate={tippyInstance.set}
      appendTo="parent"
      trigger={
        <S.SelectContainer
          aria-label={t('plan')}
          isInvalid={false}
          isDisabled={false}
          isReadonly={false}
          tabIndex={0}
          type="button"
        >
          <S.TruncatedLabelContainer
            data-testid={`plan-selector-active-plan-${activePlan.identifier}`}
          >
            {getPlanAllowances(activePlan).allowanceLabel}
          </S.TruncatedLabelContainer>
          {!hidePlanBadges && <S.SelectedPlanBadge kind={activePlan.featureGroup} size="s" />}
          <S.DropdownItemIcon icon="caret_down_alt" color="text_light" />
        </S.SelectContainer>
      }
    >
      {plans.map((plan) => (
        <DropdownMenu.BaseItem
          key={plan.identifier}
          isSelected={plan.identifier === activePlanId}
          onClick={() => {
            tippyInstance?.close();
            onChange(plan);
          }}
          title={getPlanAllowances(plan).allowanceLabel}
          data-testid={`plan-selector-item-${plan.identifier}`}
        >
          <S.ItemContainer spacing="x1">
            <S.TruncatedLabelContainer>
              {getPlanAllowances(plan).allowanceLabel}
            </S.TruncatedLabelContainer>
            {plan.identifier === activePlanId ? (
              <span data-testid="plan-selector-active-plan-icon">
                <S.DropdownItemIcon icon="selected" />
              </span>
            ) : (
              !hidePlanBadges && <PlanBadge kind={plan.featureGroup} size="s" />
            )}
          </S.ItemContainer>
        </DropdownMenu.BaseItem>
      ))}
      {scaleNotSelfServe && (
        <>
          <Divider />
          <S.Footer>
            <S.ItemContainer spacing="x1">
              <S.TruncatedLabelContainer>{tContactSales('description')}</S.TruncatedLabelContainer>
              <Button
                as="a"
                kind="secondary"
                size="s"
                href={getSalesLink('BillingPage')}
                isExternal
                title={tContactSales('title')}
              >
                {tContactSales('action')}
              </Button>
            </S.ItemContainer>
          </S.Footer>
        </>
      )}
    </S.StyledDropdownMenu>
  );
};
